// apps/frontend/src/app/shared/chat/cs-chat.state.service.ts
import {Injectable, computed, inject, signal} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {ChatbotControllerService} from '@frontend/shared';
import {UserStateService} from '../services/user-state.service';
import {TranslocoService} from '@jsverse/transloco';

type Role = 'user' | 'bot';

export interface ChatMessage {
  role: Role;
  text: string;
  createdAt: string;
}

interface StoredChatState {
  messages: ChatMessage[];
  draft: string;
  isOpen: boolean;
}

@Injectable({providedIn: 'root'})
export class ChatBotStateService {
  private readonly messagesSignal = signal<ChatMessage[]>([]);
  private readonly draftSignal = signal<string>('');
  private readonly openSignal = signal<boolean>(false);
  private readonly loadingSignal = signal<boolean>(false);

  readonly messages = computed(() => this.messagesSignal());
  readonly draft = computed(() => this.draftSignal());
  readonly isOpen = computed(() => this.openSignal());
  readonly isLoading = computed(() => this.loadingSignal());

  private readonly chatbotController: ChatbotControllerService = inject(ChatbotControllerService);
  private readonly userService: UserStateService = inject(UserStateService);
  private readonly transloco = inject(TranslocoService);

  private readonly guestSessionIdStorageKey = 'cs-chat:guest-session-id';
  private readonly guestSessionId = this.resolveGuestSessionId();

  private hydratedKey: string | null = null;

  private resolveGuestSessionId(): string {
    const existing = sessionStorage.getItem(this.guestSessionIdStorageKey);
    if (existing) return existing;

    const created = crypto.randomUUID();
    sessionStorage.setItem(this.guestSessionIdStorageKey, created);
    return created;
  }

  private getCurrentUserId(): string {
    return this.userService.getCurrentUserKeycloakId() || this.guestSessionId;
  }

  private get storageKey(): string {
    return `cs-chat:${this.getCurrentUserId()}`;
  }

  private get welcomeShownKey(): string {
    return `cs-chat:${this.getCurrentUserId()}:welcome-shown`;
  }

  constructor() {
    this.ensureHydratedForCurrentKey();
  }

  private ensureHydratedForCurrentKey(): void {
    const key = this.storageKey;
    if (this.hydratedKey === key) return;

    this.hydratedKey = key;
    const raw = sessionStorage.getItem(key);

    if (!raw) {
      this.messagesSignal.set([]);
      this.draftSignal.set('');
      this.openSignal.set(false);
      return;
    }

    try {
      const parsed = JSON.parse(raw) as StoredChatState;
      this.messagesSignal.set(parsed.messages ?? []);
      this.draftSignal.set(parsed.draft ?? '');
      this.openSignal.set(parsed.isOpen ?? false);
    } catch {
      this.messagesSignal.set([]);
      this.draftSignal.set('');
      this.openSignal.set(false);
    }
  }

  setOpen(open: boolean): void {
    this.ensureHydratedForCurrentKey();
    this.openSignal.set(open);

    if (open) {
      this.addWelcomeMessageIfNeeded();
    }

    this.persist();
  }

  setDraft(value: string): void {
    this.ensureHydratedForCurrentKey();
    this.draftSignal.set(value);
    this.persist();
  }

  async sendMessage(): Promise<void> {
    this.ensureHydratedForCurrentKey();

    const text = this.draftSignal().trim();
    if (!text || this.loadingSignal()) return;

    const userMsg: ChatMessage = {
      role: 'user',
      text,
      createdAt: new Date().toISOString(),
    };

    this.messagesSignal.update((m) => [...m, userMsg]);
    this.draftSignal.set('');
    this.loadingSignal.set(true);
    this.persist();

    try {
      const response = await firstValueFrom(
        this.chatbotController.chat({
          chatRequest: {message: text},
        }),
      );

      const botMsg: ChatMessage = {
        role: 'bot',
        text: response.response ?? '',
        createdAt: new Date().toISOString(),
      };

      this.messagesSignal.update((m) => [...m, botMsg]);
    } catch {
      this.messagesSignal.update((m) => [
        ...m,
        {
          role: 'bot',
          text: this.transloco.translate('chat.errors.unreachable'),
          createdAt: new Date().toISOString(),
        },
      ]);
    } finally {
      this.loadingSignal.set(false);
      this.persist();
    }
  }

  clear(): void {
    this.ensureHydratedForCurrentKey();
    this.messagesSignal.set([]);
    this.draftSignal.set('');
    this.openSignal.set(false);
    sessionStorage.removeItem(this.welcomeShownKey);
    this.persist();
  }

  private addWelcomeMessageIfNeeded(): void {
    const alreadyShown = sessionStorage.getItem(this.welcomeShownKey) === 'true';
    if (alreadyShown) return;

    const welcomeMessage: ChatMessage = {
      role: 'bot',
      text: this.transloco.translate('chat.welcomeMessage'),
      createdAt: new Date().toISOString(),
    };

    this.messagesSignal.update((messages) => [...messages, welcomeMessage]);
    sessionStorage.setItem(this.welcomeShownKey, 'true');
  }

  private persist(): void {
    this.ensureHydratedForCurrentKey();

    const payload: StoredChatState = {
      messages: this.messagesSignal(),
      draft: this.draftSignal(),
      isOpen: this.openSignal(),
    };
    sessionStorage.setItem(this.storageKey, JSON.stringify(payload));
  }
}
